import React, { useEffect, useRef, useState } from "react";
import {
  getMeasureReportDoc,
  getMeasureReportingByInstitution,
  getMeasureReportingByInstitutionCluster,
  getMeasuresCount,
  getMeasuresForReporting,
  getMeasuresReportCounts,
  getReportingMeasuresByQuarter,
  lockEditingStatus,
  updateMeasureNote,
} from "../../../server/server";
import { notif } from "../../utils/util";
import Counter from "./subComponents/Counter";
import DetailedMeasuresCounter from "./subComponents/MeasureCounter";
import {
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Stack,
  Tooltip,
} from "@mui/material";
import FilterForm from "./subComponents/FilterForm";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import { useUser } from "../../../auth/UseUser";
import NationalMeasureRelationModal from "./subComponents/MeasuresModals/NationalMeasureRelationModal";
import EuRelationModal from "./subComponents/EuRelationModal";
import NegDocsMeasureModal from "./subComponents/MeasuresModals/NegDocsMeasureModal";
import PointersModal from "./subComponents/MeasuresModals/PointersModal";
import EditNoteModal from "./subComponents/EditNoteModal";
import RealisationStatusModal from "./subComponents/MeasuresModals/RealisationStatusModal";
import CustomTableFooter from "../../../common/CustomTableFooter";
import HelpButton from "../../../common/HelpButton";
import HelpPageModal from "../../modals/HelpPageModal";
import { useLocation } from "react-router-dom";
import { FaPencilAlt, FaInfoCircle } from "react-icons/fa";
import download from "downloadjs";
import StratDocsMeasureModal from "./subComponents/MeasuresModals/MeasureStratDocsModal";

const MeasuresReport = () => {
  const { user } = useUser();
  const isAdmin = user?.roleView?.name.includes("Администратор");
  const isCoordinator = user?.roleView?.name.includes("Координатор");
  const isDataEntryOrSecretary =
    user?.roleView?.name.includes("Уносилац података") ||
    user?.roleView?.name.includes("Секретар");

  const location = useLocation();
  const reportId = location.pathname.split("/")[3];

  const [counts, setCounts] = useState<{
    quarter?: string;
    totalPlannedCount: number;
    realisedCount: number;
    previousQuarterUnrealisedCount: number;
    totalUnrealisedCount: number;
  } | null>(null);

  const [detailedCounts, setDetailedCounts] = useState<{
    quarter: string;
    totalPlanned: {
      currentQuarter: number;
      continuous: number;
      untilEU: number;
      total: number;
    };
    realisedCurrentQuarter: {
      currentQuarter: number;
      continuous: number;
      untilEU: number;
      total: number;
    };
    unrealisedCurrentQuarter: number;
    unrealisedFromStart: {
      quarterly: number;
      continuous: number;
      untilEU: number;
      total: number;
    };
  } | null>(null);

  const [countsForFilter, setCountsForFilter] = useState<any>({
    totalPlannedCount: 0,
    unrealisedCount: 0,
  });
  const [term, setTerm] = useState<string>("");
  const [defaultInstitution, setDefaultInstitution] = useState<any | null>(
    user?.institution && user?.roleView?.name !== "Администратор"
      ? user.institution
      : null,
  );
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowCountState, setRowCountState] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [registers, setRegisters] = useState<any[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [status, setStatus] = useState<"planned" | "unrealised" | "">(
    "planned",
  );
  const [itemToChange, setItemToChange] = useState<any>(null);
  const [showNationalMeasuresModal, setShowNationalMeasuresModal] =
    useState<boolean>(false);
  const [showCelexModal, setShowCelexModal] = useState<boolean>(false);
  const [showNegDocsModal, setShowNegDocsModal] = useState<boolean>(false);
  const [showPointersModal, setShowPointersModal] = useState<boolean>(false);
  const [showEditNoteModal, setShowEditNoteModal] = useState<boolean>(false);
  const [showRealisationStatusModal, setShowRealisationStatusModal] =
    useState<boolean>(false);
  const [helpModal, setHelpModal] = useState<boolean>(false);
  const scrollRef = useRef<any>(null);
  const [sortField, setSortField] = useState("");
  const [statusSortMode, setStatusSortMode] = useState<number | null>(null); // 0,1,2 ili null
  const [deadlineSortMode, setDeadlineSortMode] = useState<number | null>(null); // 0,1,2 ili null
  const [editDisabled, setEditDisabled] = useState<boolean>(false);
  const [selectedQuarter, setSelectedQuarter] = useState<string>("");
  const [fileLoading, setFileLoading] = useState<boolean>(false);
  const [reportType, setReportType] = useState<string>("cluster");
  const [showStratDocModal, setShowStratDocModal] = useState<boolean>(false);

  useEffect(() => {
    if (scrollRef?.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [page]);

  const renderTruncated = (value: any) => {
    const v = value === null || value === undefined ? "" : value;
    const text = typeof v === "object" ? JSON.stringify(v) : String(v);
    return (
      <Tooltip title={text}>
        <div
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "100%",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            lineHeight: "1.2em",
            maxHeight: "2.4em",
          }}
        >
          {text}
        </div>
      </Tooltip>
    );
  };

  const handleSaveNote = async (data: {
    id: number;
    note: string;
    reportId: string;
  }) => {
    const resp = await updateMeasureNote(data);

    if (resp.status === 201) {
      notif("Напомена је успешно сачувана", "success");
      setShowEditNoteModal(false);
      setItemToChange(null);
      fetchRegisters();
      return;
    }
    notif("Грешка при чувању напомене", "error");
  };

  const fetchCounts = async () => {
    const resp = await getMeasuresReportCounts(reportId, user?.roleView?.name);
    if (resp.status === 200) {
      const data = resp.data ?? resp;

      if (data.totalPlanned && typeof data.totalPlanned === "object") {
        setDetailedCounts(data);
        setCounts({
          quarter: data.quarter,
          totalPlannedCount: data.totalPlanned.total,
          realisedCount: data.realisedCurrentQuarter.total,
          previousQuarterUnrealisedCount: data.unrealisedFromStart.total,
          totalUnrealisedCount: data.unrealisedCurrentQuarter,
        });
      } else {
        setCounts(data);
        setDetailedCounts(null);
      }
    } else {
      setCounts({
        quarter: "",
        totalPlannedCount: 0,
        realisedCount: 0,
        previousQuarterUnrealisedCount: 0,
        totalUnrealisedCount: 0,
      });
      setDetailedCounts(null);
      notif("Грешка при добивању података о мерама", "error");
    }
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  const handleInstitutionChange = (event: any, newValue: any) => {
    setDefaultInstitution(newValue as any);
  };

  const handleCellClick = (params: any) => {
    if (editDisabled) {
      notif("Извештавање је закључано за уређивање.", "warning");
      return;
    }
    const clickedItem = params.row;
    const itemStatus = clickedItem?.status;
    const itemCreatedBy = clickedItem?.measureCreatedBy;
    const editableFields = new Set([
      "measureNationalMeasures",
      "measureStatus",
      "measureCelex",
      "measureNegotiationDocs",
      "measurePointers",
      "measureNote",
    ]);

    if (!isAdmin && !isCoordinator && editableFields.has(params.field)) {
      if (isDataEntryOrSecretary) {
        if (itemCreatedBy !== user?.username) {
          notif(`Не можете уређивати ову меру: нисте аутор уноса`, "warning");
          return;
        }
      } else {
        if (itemCreatedBy !== user?.username) {
          notif(
            "Не можете уређивати ову меру: немате довољно права (само администратор/координатор/аутор могу да уређују).",
            "warning",
          );
          return;
        }
      }
    }
    const rowInst = params.row?.institution;
    const userInst = user?.institution;

    const institutionsMissing = !rowInst || !userInst;

    if (
      itemStatus === "Реализовано" &&
      editableFields.has(params.field) &&
      !isAdmin &&
      (institutionsMissing || !(isCoordinator && rowInst.id === userInst.id))
    ) {
      notif("Није могуће уређивати меру која је реализована", "warning");
      return;
    }

    switch (params.field) {
      case "measureNationalMeasures":
        setItemToChange(clickedItem);
        setShowNationalMeasuresModal(true);
        break;
      case "measureCelex":
        setItemToChange(clickedItem);
        setShowCelexModal(true);
        break;
      case "measureNegotiationDocs":
        setItemToChange(clickedItem);
        setShowNegDocsModal(true);
        break;
      case "measurePointers":
        setItemToChange(clickedItem);
        setShowPointersModal(true);
        break;
      case "measureNote":
        setItemToChange(clickedItem);
        setShowEditNoteModal(true);
        break;
      case "measureStatus":
        setItemToChange(clickedItem);
        setShowRealisationStatusModal(true);
        break;
      case "stratDoc":
        setItemToChange(clickedItem);
        setShowStratDocModal(true);
        break;
      default:
        break;
    }
  };

  const columns: GridColDef[] = [
    {
      field: "measureName",
      headerName: "Назив мере",
      flex: 1,
      minWidth: 200,

      renderCell: (params) => (
        <div className="flex flex-row gap-2 items-center">
          {renderTruncated(params.row?.measureName ?? params.value)}
          <Tooltip
            title={
              params.row?.creatorName
                ? `Уносилац мере: ${params.row.creatorName}`
                : "Без податка о аутору"
            }
            arrow
          >
            <span className="inline-flex items-center cursor-help">
              <FaInfoCircle />
            </span>
          </Tooltip>
        </div>
      ),
    },
    {
      field: "institutionName",
      headerName: "Првонадлежна институција",
      width: 180,

      renderCell: (params) => (
        <Tooltip title={params.row?.institutionName ?? "-"}>
          <div
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              lineHeight: "1.2em",
              maxHeight: "2.4em",
            }}
          >
            {params.row?.institutionAbbreviation ?? "-"}
          </div>
        </Tooltip>
      ),
    },
    {
      field: "measureDeadline",
      headerName: "Рок",
      width: 150,
      sortable: true,

      renderCell: (params) =>
        renderTruncated(params.value ?? params.row?.measureDeadline),
    },
    {
      field: "measureNationalMeasures",
      headerName: "Веза са националним прописима",
      flex: 1,

      renderCell: (params) => (
        <div>
          {renderTruncated(
            params.row?.measureNationalMeasures ?? params.value ?? "",
          )}
        </div>
      ),
    },
    {
      field: "measureCelex",
      headerName: "Веза са европским прописима (CELEX)",
      flex: 1,

      renderCell: (params) =>
        renderTruncated(params.row?.measureCelex ?? params.value ?? "-"),
    },
    {
      field: "measureNegotiationDocs",
      headerName: "Веза са преговарачким документом",
      flex: 1,

      renderCell: (params) => {
        const value = params.row?.measureNegotiationDocs ?? params.value ?? "";
        return renderTruncated(value || "-");
      },
    },
    {
      field: "stratDoc",
      headerName: "Веза са стратешким документом",
      sortable: false,
      flex: 1,

      renderCell: (params) =>
        renderTruncated(params.row?.stratDoc ?? params.value ?? "-"),
    },
    {
      field: "pirv",
      headerName: "Веза са ПИРВ",
      sortable: false,
      renderCell: (params) =>
        renderTruncated(params.row?.pirv ?? params.value ?? "-"),
    },
    {
      field: "measureStatus",
      headerName: "Статус",
      width: 160,
      sortable: true,
      renderCell: (params) => (
        <div className="flex flex-row items-center justify-between w-full">
          {renderTruncated(params.value ?? params.row?.measureStatus ?? "")}
          {canUserEditRow(params.row) && <FaPencilAlt className="ml-2" />}
        </div>
      ),
    },
    {
      field: "lastUpdate",
      headerName: "Последња измена",
      width: 170,
      sortable: false,
      renderCell: (params) =>
        params.value
          ? new Date(params.value).toLocaleString("sr-RS")
          : "Без измена у кварталу",
    },
    {
      field: "measureNote",
      headerName: "Напомена",
      flex: 1,

      renderCell: (params) => {
        const v = params.row?.measureNote;

        const isEmpty =
          v === null || v === undefined || String(v).trim() === "";

        let displayText = "";
        if (isEmpty) {
          displayText = "-";
        } else if (typeof v === "object") {
          displayText = JSON.stringify(v);
        } else {
          displayText = String(v);
        }

        return (
          <div className="flex flex-row items-center justify-between w-full">
            {renderTruncated(displayText)}
            {canUserEditRow(params.row) && <FaPencilAlt className="ml-2" />}
          </div>
        );
      },
    },
  ];

  const fetchRegisters = async () => {
    setIsLoading(true);
    try {
      const limit = Number(pageSize);
      const offset = Number(page * pageSize);
      const searchString = term || "";
      const institutionId =
        defaultInstitution?.id ?? defaultInstitution?.value ?? undefined;

      const sortField =
        sortModel && sortModel.length > 0 ? sortModel[0].field : undefined;
      const sortOrder =
        sortModel && sortModel.length > 0 ? sortModel[0].sort : undefined;

      const resp = await getReportingMeasuresByQuarter(
        reportId,
        limit,
        offset,
        searchString,
        institutionId,
        status,
        sortField,
        sortOrder,
        statusSortMode,
        deadlineSortMode,
      );

      if (resp?.status === 201) {
        const data = resp.data ?? {};
        const items = data.items ?? data?.items ?? data ?? [];
        const total = data.total ?? 0;
        setEditDisabled(!data.status);

        setRegisters(items);
        setRowCountState(total);
        setCountsForFilter({
          totalPlannedCount: data.totalPlanned ?? 0,
          unrealisedCount: data.totalUnrealised ?? 0,
        });
        setSelectedQuarter(data.quarter || "");
      } else {
        notif("Грешка при учитавању списка мера", "error");
        setRegisters([]);
        setRowCountState(0);
      }
    } catch (err) {
      notif("Грешка при учитавању списка мера", "error");
      setRegisters([]);
      setRowCountState(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRegisters();
  }, [
    page,
    pageSize,
    term,
    status,
    defaultInstitution,
    sortModel,
    statusSortMode,
    deadlineSortMode,
    reportId,
  ]);

  const setPageAndCall = (newPage: number) => {
    setPage(newPage);
  };

  const canUserEditRow = (row: any) => {
    if (editDisabled) return false;
    if (isAdmin) return true;
    if (isCoordinator && row?.institutionId === user?.institution.id) {
      return true;
    }
    if (isDataEntryOrSecretary && row?.measureCreatedBy === user?.username)
      return true;
    return false;
  };

  const handleSortChange = (model: GridSortModel) => {
    const clickedField = model[0]?.field;

    if (
      clickedField !== "measureStatus" &&
      clickedField !== "measureDeadline"
    ) {
      setSortField(clickedField || "");
      setStatusSortMode(null);
      setDeadlineSortMode(null);
      setSortModel(model);
      setPage(0);
      return;
    }

    if (clickedField === "measureStatus") {
      let nextMode: number | null = null;
      if (statusSortMode === null) nextMode = 0;
      else if (statusSortMode === 0) nextMode = 1;
      else if (statusSortMode === 1) nextMode = 2;
      else nextMode = null;

      setStatusSortMode(nextMode);

      if (nextMode === null && deadlineSortMode === null) {
        setSortField("");
        setSortModel([]);
      } else {
        setSortField("measureStatus");
        setSortModel([{ field: "measureStatus", sort: "asc" }]);
      }
      setPage(0);
    }

    if (clickedField === "measureDeadline") {
      let nextMode: number | null = null;
      if (deadlineSortMode === null) nextMode = 0;
      else if (deadlineSortMode === 0) nextMode = 1;
      else if (deadlineSortMode === 1) nextMode = 2;
      else nextMode = null;

      setDeadlineSortMode(nextMode);

      if (nextMode === null && statusSortMode === null) {
        setSortField("");
        setSortModel([]);
      } else {
        setSortField("measureDeadline");
        setSortModel([{ field: "measureDeadline", sort: "asc" }]);
      }
      setPage(0);
    }
  };

  const downloadWord = async () => {
    setFileLoading(true);

    const institutionId =
      defaultInstitution?.id ?? defaultInstitution?.value ?? null;
    const searchString = term || "";

    const downloadIfOk = (resp: any, label: string) => {
      if (resp?.status === 200) {
        download(resp?.data?.data, resp?.data?.name, resp?.data?.type);
        return true;
      }
      notif(`Грешка при преузимању документа (${label}).`, "error");
      return false;
    };

    try {
      const statusesToDownload: Array<"planned" | "unrealised"> =
        status === "" ? ["planned", "unrealised"] : [status];

      let success = false;

      if (reportType === "cluster") {
        try {
          const resp = await getMeasureReportDoc(
            reportId,
            institutionId,
            searchString,
            status,
          );
          if (!downloadIfOk(resp, "cluster")) {
            notif("Документ није успешно преузет.", "error");
          }
        } catch {
          notif("Грешка при преузимању документа (cluster).", "error");
        }
        return;
      }

      if (reportType === "clusterAndInstitution") {
        for (const currentStatus of statusesToDownload) {
          try {
            const resp = await getMeasureReportingByInstitutionCluster(
              reportId,
              institutionId,
              searchString,
              currentStatus as any,
            );
            success =
              downloadIfOk(resp, `clusterAndInstitution-${currentStatus}`) ||
              success;
          } catch {
            notif(
              `Грешка при преузимању документа (clusterAndInstitution-${currentStatus}).`,
              "error",
            );
          }
        }

        if (!success) {
          notif("Ниједан документ није успешно преузет.", "error");
        }
        return;
      }

      if (reportType === "institution") {
        const resp = await getMeasureReportingByInstitution(
          reportId,
          institutionId,
          searchString,
        );
        downloadIfOk(resp, "institution");
        return;
      }

      notif("Неподржан тип извештаја за преузимање.", "warning");
    } finally {
      setFileLoading(false);
    }
  };

  return (
    <div
      className="py-10 px-6 overflow-auto gray-small-scroll h-full flex flex-col"
      ref={scrollRef}
    >
      <div className="flex gap-6 items-center justify-between pr-10">
        <h1 className="text-[#04438b] text-4xl font-bold">
          Извештавање о мерама {selectedQuarter && `- ${selectedQuarter}`}
        </h1>
        <HelpButton onClick={() => setHelpModal(true)} />
      </div>
      <div className="mt-6">
        {detailedCounts ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <DetailedMeasuresCounter
              title={`Укупно планираних мера за ${detailedCounts.quarter ?? ""}`}
              subtitle=" "
              data={detailedCounts.totalPlanned}
              colorScheme="blue"
              unrealised={true}
            />
            <DetailedMeasuresCounter
              title={`Реализовано у ${detailedCounts.quarter ?? ""}`}
              subtitle="Мере са статусом реализовано"
              data={detailedCounts.realisedCurrentQuarter}
              colorScheme="green"
              isSecond={true}
            />
            <DetailedMeasuresCounter
              title={`Нереализовано у ${detailedCounts.quarter ?? ""}`}
              subtitle={`Само кварталне мере са роком ${detailedCounts.quarter ?? ""} које нису реализоване`}
              data={{
                total: detailedCounts.unrealisedCurrentQuarter,
              }}
              colorScheme="red"
              unrealised={true}
            />
            <DetailedMeasuresCounter
              title="Нереализовано од почетка примене НПАА"
              subtitle=" "
              data={detailedCounts.unrealisedFromStart}
              colorScheme="orange"
              unrealised={true}
              isSecond={true}
            />
          </div>
        ) : counts ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(counts)
              .filter(([key]) => key !== "quarter")
              .map(([key, value]) => (
                <Counter key={key} name={key} value={value} type="measure" />
              ))}
          </div>
        ) : (
          <div className="text-gray-500">No data</div>
        )}
      </div>

      <div>
        <FilterForm
          term={term}
          setTerm={setTerm}
          defaultInstitution={defaultInstitution}
          onInstitutionChange={handleInstitutionChange}
          totalPlannedCount={countsForFilter.totalPlannedCount}
          unrealisedCount={countsForFilter.unrealisedCount}
          status={status}
          setStatus={setStatus}
          measure={true}
          fileLoading={fileLoading}
          downloadWord={downloadWord}
          quarter={selectedQuarter}
          reportType={reportType}
          setReportType={setReportType}
        />
      </div>

      <div className="mt-5 min-h-[430px] w-full">
        <DataGrid
          rowCount={rowCountState}
          loading={isLoading}
          pagination
          disableColumnMenu
          paginationMode="server"
          onPageChange={(newPage) => setPageAndCall(newPage)}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setPage(0);
          }}
          page={page}
          pageSize={pageSize}
          sortModel={sortModel}
          rows={registers}
          onCellClick={handleCellClick}
          columns={columns}
          getRowId={(row) => row.id}
          autoHeight={true}
          rowHeight={52}
          style={{ zIndex: 0 }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          disableSelectionOnClick
          sortingMode="server"
          onSortModelChange={(model) => handleSortChange(model)}
          getRowClassName={(params) => {
            const out = params?.row?.outOfForce;
            const pirv = params?.row?.pirv;
            return out ? "redRow" : pirv ? "blueRow" : "";
          }}
          getCellClassName={(params) => {
            const editableCells = new Set([
              "measureNationalMeasures",
              "measureStatus",
              "measureCelex",
              "measureNegotiationDocs",
              "measurePointers",
              "measureNote",
            ]);
            const viewOnlyCells = new Set(["stratDoc"]);

            if (viewOnlyCells.has(params.field)) return "editable-cell";

            const canEdit = canUserEditRow(params.row);
            if (!canEdit) return "";
            if (editableCells.has(params.field)) return "editable-cell";
            return "";
          }}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal",
              lineHeight: "1.2em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
            },
            "& .MuiDataGrid-columnHeader": {
              height: "auto !important",
              minHeight: "56px !important",
            },
            "& .MuiDataGrid-columnHeaders": {
              maxHeight: "100px !important",
            },
            "& .editable-cell": {
              cursor: editDisabled ? "default" : "pointer",
              padding: "0 !important",
              transition: "background-color 0.2s ease",
              paddingRight: "8px !important",
              paddingLeft: "8px !important",
              ...(!editDisabled && {
                "&:hover": {
                  backgroundColor: "#242EBF",
                  textDecoration: "underline",
                  color: "white",
                },
              }),
            },
          }}
          components={{
            Footer: CustomTableFooter,
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                Не постоје прописи са задатим критеријумом
              </Stack>
            ),
            NoResultsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                Не постоје прописи са задатим критеријумом
              </Stack>
            ),
          }}
          componentsProps={{
            footer: {
              count: rowCountState,
              page: page,
              pageSize: pageSize,
              onPageChange: (event: any, newPage: any) =>
                setPageAndCall(newPage),
              onPageSizeChange: (newPageSize: any) => setPageSize(newPageSize),
              rowsPerPageOptions: [5, 10, 20],
              loadRegisters: fetchRegisters,
            },
          }}
        />
      </div>

      {showNationalMeasuresModal && itemToChange && (
        <NationalMeasureRelationModal
          open={showNationalMeasuresModal}
          onClose={() => {
            setShowNationalMeasuresModal(false);
            setItemToChange(null);
            fetchRegisters();
          }}
          item={itemToChange}
        />
      )}

      {showCelexModal && itemToChange && (
        <EuRelationModal
          open={showCelexModal}
          onClose={() => {
            setShowCelexModal(false);
            setItemToChange(null);
            fetchRegisters();
          }}
          register={itemToChange}
          isMeasure={true}
        />
      )}

      {showNegDocsModal && itemToChange && (
        <NegDocsMeasureModal
          open={showNegDocsModal}
          onClose={() => {
            setShowNegDocsModal(false);
            setItemToChange(null);
            fetchRegisters();
          }}
          item={itemToChange}
        />
      )}

      {showPointersModal && itemToChange && (
        <PointersModal
          open={showPointersModal}
          onClose={() => {
            setShowPointersModal(false);
            setItemToChange(null);
            fetchRegisters();
          }}
          item={itemToChange}
        />
      )}

      {showEditNoteModal && itemToChange && (
        <EditNoteModal
          open={showEditNoteModal}
          item={itemToChange}
          disabled={!canUserEditRow(itemToChange)}
          onClose={() => {
            setShowEditNoteModal(false);
            setItemToChange(null);
            fetchRegisters();
          }}
          onSave={handleSaveNote}
        />
      )}
      {showRealisationStatusModal && itemToChange && (
        <RealisationStatusModal
          open={showRealisationStatusModal}
          item={itemToChange}
          onClose={() => {
            setShowRealisationStatusModal(false);
            setItemToChange(null);
            fetchCounts();
            fetchRegisters();
          }}
          disabled={!canUserEditRow(itemToChange)}
        />
      )}
      <HelpPageModal
        modalIsOpen={helpModal}
        closeModal={() => {
          setHelpModal(false);
        }}
        name={"reportingMeasure"}
      />
      {showStratDocModal && itemToChange && (
        <StratDocsMeasureModal
          open={showStratDocModal}
          item={itemToChange}
          onClose={() => {
            setShowStratDocModal(false);
            setItemToChange(null);
          }}
        />
      )}
    </div>
  );
};

export default MeasuresReport;
